import { useEffect, useRef, useState } from 'preact/hooks';
import { db, type Attempt, type Card } from '../data/db';
import { appState } from '../app/state';

interface Stats {
  masteredThisWeek: number;
  toneAccuracyKnown: boolean;
  timeMinutesThisWeek: number;
  leeches: { card: Card; lapses: number }[];
  nextZoneProgressPct: number;
}

export function ParentDashboard({ onClose }: { onClose: () => void }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [tab, setTab] = useState<'metrics' | 'settings'>('metrics');
  const profile = appState.get().profile;

  useEffect(() => {
    if (!profile) return;
    loadStats(profile.id).then(setStats);
  }, [profile?.id]);

  if (!profile) return null;

  return (
    <div class="ws-overlay scrim">
      <div class="ws-panel">
        <div class="ws-row between" style={{ marginBottom: 8 }}>
          <h1 class="ws-h1">{profile.avatar} {profile.name} — Parent View</h1>
          <button class="ws-btn ghost" onClick={onClose}>Close ✕</button>
        </div>
        <div class="ws-row" style={{ marginBottom: 12 }}>
          <button class="ws-btn ghost" style={{ background: tab === 'metrics' ? 'var(--accent)' : undefined }} onClick={() => setTab('metrics')}>This week</button>
          <button class="ws-btn ghost" style={{ background: tab === 'settings' ? 'var(--accent)' : undefined }} onClick={() => setTab('settings')}>Settings</button>
          <button class="ws-btn" onClick={() => appState.goto({ kind: 'ingest' })}>＋ Add workbook page</button>
        </div>

        {tab === 'metrics' && stats && (
          <div class="ws-grid" style={{ gap: 12 }}>
            <div class="ws-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              <div class="ws-stat"><div class="num">{stats.masteredThisWeek}</div><div class="label">characters mastered (week)</div></div>
              <div class="ws-stat"><div class="num">{stats.timeMinutesThisWeek}m</div><div class="label">time on task (week)</div></div>
              <div class="ws-stat"><div class="num">{stats.nextZoneProgressPct}%</div><div class="label">progress to next zone</div></div>
              <div class="ws-stat" title="Tone-only error detection requires the Phase 1 Azure upgrade">
                <div class="num" style={{ opacity: 0.5 }}>—</div>
                <div class="label">tone accuracy (upgrade)</div>
              </div>
            </div>
            <div class="ws-card">
              <strong style={{ display: 'block', marginBottom: 8 }}>Stuck words ({stats.leeches.length})</strong>
              {stats.leeches.length === 0 && <div class="ws-muted">Nothing's stuck yet — keep playing.</div>}
              {stats.leeches.map(({ card, lapses }) => (
                <div key={card.id} class="ws-row between" style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div>
                    <span style={{ fontSize: 24, marginRight: 8 }}>{card.hanzi}</span>
                    <span class="ws-muted">{card.pinyin} · {card.english}</span>
                  </div>
                  <span class="ws-muted">{lapses} miss{lapses === 1 ? '' : 'es'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'settings' && <SettingsPanel />}
      </div>
    </div>
  );
}

async function loadStats(profileId: string): Promise<Stats> {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const srsList = await db.srs.where('profileId').equals(profileId).toArray();
  const attempts = await db.attempts.where('profileId').equals(profileId).and((a: Attempt) => a.ts >= weekAgo).toArray();

  const masteredThisWeek = srsList.filter((s) => s.intervalDays >= 7 && s.lastAttemptAccuracy >= 80 && s.lastSeenAt >= weekAgo).length;

  // Approximate time-on-task: 6s per attempt is a reasonable default for combat encounters.
  const timeMinutesThisWeek = Math.round((attempts.length * 6) / 60);

  // Leeches: top 5 by lapse count.
  const leechRaw = [...srsList].sort((a, b) => b.lapses - a.lapses).filter((s) => s.lapses > 0).slice(0, 5);
  const cardIds = leechRaw.map((s) => s.cardId);
  const cards = await db.cards.where('id').anyOf(cardIds).toArray();
  const cardById = new Map(cards.map((c) => [c.id, c] as const));
  const leeches = leechRaw
    .map((s) => ({ card: cardById.get(s.cardId), lapses: s.lapses }))
    .filter((x): x is { card: Card; lapses: number } => !!x.card);

  // Next-zone readiness: % of cards in any not-yet-unlocked lesson at interval≥3 AND accuracy≥70.
  const progress = await db.progress.get(profileId);
  const lessons = await db.lessons.toArray();
  const lockedLessons = lessons.filter((l) => !progress?.unlockedZoneIds.includes(l.id));
  const nextLesson = lockedLessons[0] ?? lessons.find((l) => l.id !== progress?.unlockedZoneIds.at(-1));
  let nextZoneProgressPct = 0;
  if (nextLesson) {
    const lessonCards = await db.cards.where('lessonId').equals(nextLesson.id).toArray();
    if (lessonCards.length) {
      const srsById = new Map(srsList.map((s) => [s.cardId, s] as const));
      const mastered = lessonCards.filter((c) => {
        const s = srsById.get(c.id);
        return s && s.intervalDays >= 3 && s.lastAttemptAccuracy >= 70;
      }).length;
      nextZoneProgressPct = Math.round((mastered / lessonCards.length) * 100);
    }
  }

  return {
    masteredThisWeek,
    toneAccuracyKnown: false, // Phase 1 only
    timeMinutesThisWeek,
    leeches,
    nextZoneProgressPct,
  };
}

function SettingsPanel() {
  const profile = appState.get().profile;
  const [settings, setSettings] = useState(appState.get().settings);
  const [azureKey, setAzureKey] = useState(settings?.azureKey ?? '');
  const [azureRegion, setAzureRegion] = useState(settings?.azureRegion ?? 'eastasia');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string>('');
  const [showSpeakStep, setShowSpeakStep] = useState(false);
  if (!settings || !profile) return null;

  const update = async (patch: Partial<typeof settings>) => {
    const next = { ...settings, ...patch, key: 'global' as const };
    await db.settings.put(next);
    setSettings(next);
    appState.set({ settings: next });
  };

  // Reused across press-and-hold so the same recording starts on press and
  // ends on release. Kept on the closure because the diagnostic spans a UI gesture.
  const speakClient = useRef<{ client: import('../speech/azureClient').AzureSpeechClient | null; step2: string } | null>(null);

  // Step 1 + 2 only — preflight. After this passes, a press-and-hold button
  // appears for Step 3 (the actual speak test).
  const testAuthAndMic = async () => {
    setTesting(true);
    setShowSpeakStep(false);
    setTestResult('Step 1/2 — validating key + region…');
    try {
      const { validateConfig } = await import('../speech/azureClient');
      const { ensureMicAccess } = await import('../speech/mic');

      const v = await validateConfig({ key: azureKey, region: azureRegion });
      if (!v.ok) {
        setTestResult(
          `Step 1/2 — ✗ Key/region check failed (HTTP ${v.status} ${v.statusText}).\n` +
          (v.hint ? `Hint: ${v.hint}\n` : '') +
          (v.body ? `Azure said: ${v.body}` : '')
        );
        return;
      }
      const step1 = `Step 1/2 — ✓ Auth OK (key + region accepted).`;
      setTestResult(`${step1}\nStep 2/2 — checking microphone…`);

      const mic = await ensureMicAccess();
      if (mic.state !== 'granted') {
        setTestResult(
          `${step1}\nStep 2/2 — ✗ Microphone not granted (state: ${mic.state}).\n` +
          (mic.error ? `${mic.error}\n` : '') +
          (mic.state === 'denied' ? 'Open browser/OS settings and allow microphone access for this site, then retry.' : 'Tap Allow when the browser prompts.')
        );
        return;
      }

      setTestResult(
        `${step1}\nStep 2/2 — ✓ Microphone granted.\n\n` +
        `Now press and hold the button below, say 你好 (nǐ hǎo), then release.`
      );
      speakClient.current = { client: null, step2: `${step1}\nStep 2/2 — ✓ Microphone granted.` };
      setShowSpeakStep(true);
    } catch (e) {
      setTestResult(`✗ Unexpected error: ${(e as Error).message}`);
    } finally {
      setTesting(false);
    }
  };

  const beginSpeakTest = async () => {
    if (!speakClient.current) return;
    const { AzureSpeechClient } = await import('../speech/azureClient');
    const client = new AzureSpeechClient({ key: azureKey, region: azureRegion });
    speakClient.current.client = client;
    try {
      await client.startRecording();
      setTestResult(`${speakClient.current.step2}\n🎤 Recording — say 你好 now, then release.`);
    } catch (e) {
      setTestResult(`${speakClient.current.step2}\n✗ Couldn't start recording: ${(e as Error).message}`);
      speakClient.current.client = null;
    }
  };

  const endSpeakTest = async () => {
    const wrap = speakClient.current;
    if (!wrap?.client) return;
    const client = wrap.client;
    wrap.client = null;
    setTestResult(`${wrap.step2}\nUploading audio to Azure…`);
    const result = await client.stopAndAssess('你好');
    if (result === null) {
      setTestResult(
        `${wrap.step2}\n✗ Azure rejected the audio: ${client.lastError}\n` +
        `Possible causes: malformed audio, network blip, or quota.`
      );
      return;
    }
    if (!result.transcript) {
      setTestResult(
        `${wrap.step2}\n⚠ Azure heard silence (recorded ${result.audioMs}ms).\n` +
        `Either the mic isn't capturing audio, or the speaking volume was too low. Try again louder.`
      );
      return;
    }
    setTestResult(
      `${wrap.step2}\n✓ Heard: "${result.transcript}"\n` +
      `Pronunciation ${Math.round(result.accuracy)}/100 · fluency ${Math.round(result.fluency)} · completeness ${Math.round(result.completeness)}\n` +
      `Recorded ${result.audioMs}ms of audio. Combat is ready.`
    );
  };

  return (
    <div class="ws-grid" style={{ gap: 12 }}>
      <div class="ws-card">
        <strong>Pinyin reveal</strong>
        <div class="ws-muted">When should the pinyin under the hanzi appear?</div>
        <div class="ws-row" style={{ marginTop: 8 }}>
          {(['on-miss', 'always', 'never'] as const).map((v) => (
            <button key={v} class="ws-btn ghost" style={{ background: settings!.pinyinReveal === v ? 'var(--accent)' : undefined }} onClick={() => update({ pinyinReveal: v })}>{v}</button>
          ))}
        </div>
      </div>

      <div class="ws-card">
        <strong>Speech recognition</strong>
        <div class="ws-muted" style={{ marginBottom: 10, fontSize: 13 }}>
          Web Speech (free, built into Safari) <strong>does not work for Mandarin on iPad</strong> — it captures audio but returns nothing. <strong>Azure Pronunciation Assessment</strong> is the only option with tone scoring. Free tier covers ~one kid playing 30 min/day.
        </div>

        <div class="ws-row" style={{ marginBottom: 10 }}>
          {(['web-speech', 'azure'] as const).map((v) => (
            <button
              key={v}
              class="ws-btn ghost"
              style={{ background: settings.asrProvider === v ? 'var(--accent)' : undefined }}
              onClick={() => update({ asrProvider: v })}
            >
              {v === 'azure' ? 'Azure (recommended)' : 'Web Speech (broken on iPad)'}
            </button>
          ))}
        </div>

        {settings.asrProvider === 'azure' && (
          <div class="ws-grid" style={{ gap: 8 }}>
            <div>
              <div class="ws-muted" style={{ fontSize: 12, marginBottom: 4 }}>Azure region</div>
              <select class="ws-input" value={azureRegion} onChange={(e) => setAzureRegion((e.target as HTMLSelectElement).value)}>
                <option value="eastasia">East Asia (Hong Kong nearby)</option>
                <option value="southeastasia">Southeast Asia (Singapore)</option>
                <option value="eastus">East US</option>
                <option value="westus2">West US 2</option>
                <option value="westeurope">West Europe</option>
                <option value="japaneast">Japan East</option>
              </select>
            </div>
            <div>
              <div class="ws-muted" style={{ fontSize: 12, marginBottom: 4 }}>Azure speech key</div>
              <div class="ws-row" style={{ gap: 6 }}>
                <input
                  class="ws-input"
                  type={showKey ? 'text' : 'password'}
                  placeholder="paste your key from Azure portal"
                  value={azureKey}
                  onInput={(e) => setAzureKey((e.target as HTMLInputElement).value)}
                  style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}
                />
                <button class="ws-btn ghost" onClick={() => setShowKey(!showKey)}>{showKey ? '🙈' : '👁'}</button>
              </div>
            </div>
            <div class="ws-row" style={{ gap: 8 }}>
              <button class="ws-btn" onClick={() => { update({ azureKey, azureRegion }); setTestResult('Saved.'); }} disabled={!azureKey}>Save</button>
              <button class="ws-btn ghost" onClick={testAuthAndMic} disabled={!azureKey || testing}>Test connection</button>
            </div>
            {showSpeakStep && (
              <button
                class="ws-btn"
                style={{ background: 'var(--accent)', marginTop: 4 }}
                onTouchStart={(e) => { e.preventDefault(); beginSpeakTest(); }}
                onTouchEnd={(e) => { e.preventDefault(); endSpeakTest(); }}
                onMouseDown={beginSpeakTest}
                onMouseUp={endSpeakTest}
              >
                🎤  Press & hold — say 你好 — release
              </button>
            )}
            {testResult && (
              <div class="ws-muted" style={{ fontSize: 12, padding: 8, background: 'var(--panel-2)', borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {testResult}
              </div>
            )}
            <details>
              <summary class="ws-muted" style={{ cursor: 'pointer', fontSize: 12 }}>How to get an Azure key</summary>
              <div class="ws-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.6 }}>
                <ol style={{ paddingLeft: 18 }}>
                  <li>Go to <strong>portal.azure.com</strong> and sign in (free).</li>
                  <li>Click <strong>Create a resource</strong> → search <strong>"Speech"</strong> → pick the Microsoft one → <strong>Create</strong>.</li>
                  <li>Resource group: create one named anything (e.g. "speech"). Region: <strong>East Asia</strong>. Name: <strong>wizardspeak</strong> or any. Pricing tier: <strong>Free F0</strong>.</li>
                  <li>After it deploys, click <strong>Go to resource</strong> → <strong>Keys and Endpoint</strong> in the sidebar.</li>
                  <li>Copy <strong>KEY 1</strong>. Paste it above. Region is already preset.</li>
                  <li>Tap <strong>Save</strong>, then <strong>Test connection</strong>.</li>
                </ol>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
